import { useState, useEffect, useRef } from "react";
import axios from "axios";
import TermsModal from "./components/TermsModal";
import { 
  CreditCard, 
  Building2, 
  Wallet, 
  Network, 
  DollarSign, 
  Store,
  Zap,
  Headphones,
  Settings,
  TrendingUp,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Ban,
  MessageCircle,
  Phone,
  Rocket,
  Menu,
  X,
  ChevronRight,
  Check,
  Loader2,
  User,
  LogOut,
  Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Particle Network Component
const ParticleNetwork = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Create particles
    const particleCount = 50;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1
      });
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw particles
      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(6, 182, 212, 0.5)';
        ctx.fill();
        
        // Draw connections
        particles.forEach((particle2, j) => {
          if (i === j) return;
          const dx = particle.x - particle2.x;
          const dy = particle.y - particle2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particle2.x, particle2.y);
            ctx.strokeStyle = `rgba(6, 182, 212, ${0.15 * (1 - distance / 150)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="particle-network">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
};

// Auth Context Helper
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('morphyng_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('morphyng_token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (err) {
          localStorage.removeItem('morphyng_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('morphyng_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('morphyng_token');
    setToken(null);
    setUser(null);
  };

  return { user, token, loading, login, logout, isAuthenticated: !!token };
};

// Navigation Component
const Navigation = ({ onLoginClick, isAuthenticated, onDashboardClick, onLogout, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "Servicios", href: "#servicios" },
    { name: "Ventajas", href: "#ventajas" },
    { name: "Contacto", href: "#contacto" }
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'nav-blur border-b border-slate-800' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-28">
          <a 
            href="#" 
            data-testid="logo-link"
            className="flex items-center"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_payment-service-6/artifacts/m8pyl8h2_Logo_1.png" 
              alt="Morphyng"
              className="w-auto"
              style={{ height: '100px' }}
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className="text-slate-400 hover:text-white transition-colors duration-200"
              >
                {link.name}
              </a>
            ))}
            
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <button
                  onClick={onDashboardClick}
                  data-testid="nav-dashboard-btn"
                  className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-2"
                >
                  <User size={18} />
                  {user?.company_name || 'Mi Cuenta'}
                </button>
                <button
                  onClick={onLogout}
                  data-testid="nav-logout-btn"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <button
                onClick={onLoginClick}
                data-testid="nav-login-btn"
                className="btn-primary text-sm px-6 py-3 flex items-center gap-2"
              >
                <Lock size={16} />
                Zona Clientes
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden glass-card p-6 mb-4"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                onClick={() => setIsOpen(false)}
                className="block py-3 text-slate-400 hover:text-white transition-colors"
              >
                {link.name}
              </a>
            ))}
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => { onDashboardClick(); setIsOpen(false); }}
                  data-testid="mobile-dashboard-btn"
                  className="block py-3 text-cyan-400 hover:text-cyan-300 transition-colors w-full text-left"
                >
                  Mi Cuenta
                </button>
                <button
                  onClick={() => { onLogout(); setIsOpen(false); }}
                  data-testid="mobile-logout-btn"
                  className="block py-3 text-slate-400 hover:text-white transition-colors w-full text-left"
                >
                  Cerrar Sesion
                </button>
              </>
            ) : (
              <button
                onClick={() => { onLoginClick(); setIsOpen(false); }}
                data-testid="mobile-login-btn"
                className="btn-primary block text-center mt-4 w-full"
              >
                Zona Clientes
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Aurora Background */}
      <div className="aurora-bg absolute inset-0"></div>
      
      {/* Particle Network */}
      <ParticleNetwork />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="relative z-10">
            <div className="animate-fade-in-up stagger-1">
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-6">
                Tu Socio en Pagos
              </span>
            </div>
            
            <h1 
              data-testid="hero-headline"
              className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-6 animate-fade-in-up stagger-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Soluciones de Pago para tu{" "}
              <span className="gradient-text">Negocio</span>
            </h1>
            
            <p 
              data-testid="hero-subheadline"
              className="text-lg md:text-xl text-slate-400 mb-8 leading-relaxed max-w-xl animate-fade-in-up stagger-3"
            >
              Impulsamos el crecimiento de comercios y PyMEs con soluciones de pago simples, 
              rapidas y accesibles. PSP as a Service, billeteras virtuales y mucho mas.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-4">
              <a
                href="#contacto"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                Contactanos
                <ChevronRight size={18} />
              </a>
              <a
                href="#servicios"
                data-testid="hero-cta-secondary"
                className="px-8 py-3.5 rounded-full border border-slate-700 text-white hover:bg-slate-800 transition-all duration-300 inline-flex items-center justify-center"
              >
                Ver Servicios
              </a>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative animate-fade-in-up stagger-3 hidden md:block">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-indigo-500/20 rounded-3xl blur-3xl"></div>
            <img
              src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80"
              alt="Soluciones de pago para comercios"
              data-testid="hero-image"
              className="relative z-10 rounded-3xl hero-image-mask w-full"
            />
          </div>
        </div>
        
        {/* Disclaimer note */}
        <div className="mt-12 text-center animate-fade-in-up stagger-5">
          <p className="text-slate-500 text-sm">
            * Todas las operaciones realizadas a traves de nuestra plataforma son en pesos argentinos (ARS).
          </p>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      title: "Obtene tu CVU con nosotros",
      desc: "Recibi y realiza transferencias y pagos con tu cuenta MORPHYNG. Todo lo que necesitas para operar de forma simple y segura.",
      span: "md:col-span-3",
      icon: Wallet,
      hasImage: true,
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&q=80",
      isFeatured: true
    },
    {
      title: "PSP as a Service",
      desc: "Tu propia pasarela de pagos con nuestra tecnologia. Tu marca, nuestra infraestructura.",
      span: "md:col-span-2",
      icon: Network,
      hasImage: true,
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80"
    },
    {
      title: "Soluciones para Comercios",
      desc: "Acepta todos los medios de pago de forma simple.",
      span: "md:col-span-1",
      icon: Store
    },
    {
      title: "Servicios para PyMEs",
      desc: "Herramientas pensadas para pequenas y medianas empresas.",
      span: "md:col-span-1",
      icon: Building2
    },
    {
      title: "Cobros y Pagos",
      desc: "Gestion integral de cobranzas y pagos a proveedores.",
      span: "md:col-span-2",
      icon: CreditCard,
      hasImage: true,
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80"
    }
  ];

  return (
    <section 
      id="servicios"
      data-testid="services-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-400 text-sm font-medium mb-4">
            Servicios
          </span>
          <h2 
            data-testid="services-title"
            className="text-3xl md:text-5xl font-semibold mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Todo lo que tu negocio <span className="text-cyan-400">necesita</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Soluciones adaptadas a comercios de todos los tamanos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              data-testid={`service-card-${index}`}
              className={`glass-card card-hover-lift group relative overflow-hidden ${service.span} ${
                service.hasImage ? (service.isFeatured ? 'min-h-[320px]' : 'min-h-[280px]') : 'p-8'
              }`}
            >
              {service.hasImage && (
                <>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent"></div>
                </>
              )}
              
              <div className={`relative z-10 ${service.hasImage ? 'absolute bottom-0 left-0 right-0 p-8' : ''}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  service.hasImage 
                    ? 'bg-cyan-500/20' 
                    : 'bg-slate-800 group-hover:bg-cyan-500/20'
                } transition-colors duration-300`}>
                  <service.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 
                  className={`font-semibold mb-2 text-white ${service.isFeatured ? 'text-2xl md:text-3xl' : 'text-xl'}`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {service.title}
                </h3>
                <p className={`text-slate-400 ${service.isFeatured ? 'text-base md:text-lg max-w-2xl' : ''}`}>
                  {service.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "Precios Accesibles",
      desc: "Tarifas competitivas y transparentes. Sin costos ocultos ni sorpresas en tu facturacion."
    },
    {
      icon: Zap,
      title: "Rapida Implementacion",
      desc: "Comienza a operar en tiempo record. Proceso de alta simple y acompanamiento personalizado."
    },
    {
      icon: Settings,
      title: "Facil Integracion",
      desc: "Conecta tu sistema de forma sencilla. Documentacion clara y soporte tecnico disponible."
    },
    {
      icon: Store,
      title: "Pensado para tu Negocio",
      desc: "Soluciones disenadas especialmente para comercios y PyMEs. Entendemos tus necesidades."
    },
    {
      icon: Headphones,
      title: "Soporte Dedicado",
      desc: "Equipo de atencion listo para ayudarte. Resolvemos tus dudas de forma rapida y eficiente."
    },
    {
      icon: CreditCard,
      title: "Todos los Medios de Pago",
      desc: "Acepta tarjetas, transferencias, billeteras digitales y mas. Todo en un solo lugar."
    }
  ];

  return (
    <section 
      id="ventajas"
      data-testid="benefits-section"
      className="py-24 md:py-32 relative"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-4">
            Ventajas
          </span>
          <h2 
            data-testid="benefits-title"
            className="text-3xl md:text-5xl font-semibold mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Por que elegir <span className="text-cyan-400">MORPHYNG</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Nos enfocamos en hacer crecer tu negocio con soluciones simples y efectivas.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.title}
              data-testid={`benefit-card-${index}`}
              className="glass-card card-hover-lift p-8 group hover:border-cyan-500/30"
            >
              <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors duration-300">
                <benefit.icon className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 
                className="text-xl font-semibold mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {benefit.title}
              </h3>
              <p className="text-slate-400 leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      icon: MessageCircle,
      title: "Contactanos",
      desc: "Escribinos y contanos sobre tu negocio. Sin compromiso."
    },
    {
      icon: Phone,
      title: "Te Asesoramos",
      desc: "Analizamos tus necesidades y te proponemos la mejor solucion."
    },
    {
      icon: Rocket,
      title: "Empeza a Cobrar",
      desc: "Implementamos rapidamente y comenzas a operar."
    }
  ];

  return (
    <section 
      id="como-funciona"
      data-testid="how-it-works-section"
      className="py-24 md:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium mb-4">
            Simple y Rapido
          </span>
          <h2 
            data-testid="how-it-works-title"
            className="text-3xl md:text-5xl font-semibold mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Como <span className="text-cyan-400">funciona</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            En solo 3 pasos podes empezar a cobrar con MORPHYNG.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
          {steps.map((step, index) => (
            <div
              key={step.title}
              data-testid={`step-card-${index}`}
              className="relative text-center"
            >
              {/* Connector line for desktop */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent z-0"></div>
              )}
              
              <div className="relative z-10">
                {/* Step number */}
                <div className="absolute -top-2 -right-2 md:right-1/4 w-8 h-8 rounded-full bg-cyan-500 text-slate-950 font-bold flex items-center justify-center text-sm z-20">
                  {index + 1}
                </div>
                
                {/* Icon */}
                <div className="w-32 h-32 mx-auto rounded-3xl bg-slate-800/50 border border-slate-700 flex items-center justify-center mb-6 group-hover:border-cyan-500/50 transition-colors">
                  <step.icon className="w-12 h-12 text-cyan-400" />
                </div>
                
                <h3 
                  className="text-xl font-semibold mb-3"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p className="text-slate-400 max-w-xs mx-auto">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      setError('Error al enviar el formulario. Por favor intenta de nuevo.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contacto"
      data-testid="contact-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-4">
              Contacto
            </span>
            <h2 
              data-testid="contact-title"
              className="text-3xl md:text-5xl font-semibold mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Listo para <span className="text-cyan-400">comenzar</span>?
            </h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Nuestro equipo esta listo para ayudarte a encontrar la solucion perfecta para tu negocio. 
              Completa el formulario y te contactaremos a la brevedad.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Check className="w-5 h-5 text-cyan-400" />
                </div>
                <span>Rapida implementacion</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Check className="w-5 h-5 text-cyan-400" />
                </div>
                <span>Soporte tecnico dedicado</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Check className="w-5 h-5 text-cyan-400" />
                </div>
                <span>Sin costos ocultos</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card p-8 md:p-10">
            {isSubmitted ? (
              <div 
                data-testid="contact-success-message"
                className="text-center py-12"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 
                  className="text-2xl font-semibold mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  Mensaje Enviado
                </h3>
                <p className="text-slate-400">
                  Gracias por contactarnos. Te responderemos pronto.
                </p>
                <button
                  data-testid="send-another-message-btn"
                  onClick={() => setIsSubmitted(false)}
                  className="mt-6 text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm text-slate-400 mb-2">
                      Nombre completo
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="contact-input-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="morphyng-input"
                      placeholder="Juan Perez"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm text-slate-400 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="contact-input-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="morphyng-input"
                      placeholder="juan@empresa.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-sm text-slate-400 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      data-testid="contact-input-company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="morphyng-input"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm text-slate-400 mb-2">
                      Mensaje
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="contact-input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="morphyng-input resize-none"
                      placeholder="Cuentanos sobre tu negocio..."
                    />
                  </div>

                  {error && (
                    <div 
                      data-testid="contact-error-message"
                      className="p-4 rounded-lg bg-red-500/10 text-red-400 text-sm"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar Mensaje
                        <ChevronRight size={18} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onOpenTerms }) => {
  const currentYear = 2025;

  return (
    <footer 
      data-testid="footer"
      className="pt-8 pb-6 border-t border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Brand */}
        <div className="text-center mb-4">
          <a 
            href="#" 
            data-testid="footer-logo"
            className="inline-block mb-3"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_payment-service-6/artifacts/m8pyl8h2_Logo_1.png" 
              alt="Morphyng"
              className="w-auto mx-auto"
              style={{ height: '200px' }}
            />
          </a>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Soluciones de pago pensadas para hacer crecer tu negocio.
          </p>
          <p className="text-slate-600 text-xs mt-2">
            * Todas las operaciones realizadas a traves de nuestra plataforma son en pesos argentinos (ARS).
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="text-center pt-4 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-sm">
            <p className="text-slate-500">
              {currentYear} MORPHYNG. Todos los derechos reservados.
            </p>
            <button
              data-testid="footer-terms-link"
              onClick={onOpenTerms}
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              Terminos y Condiciones
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Login Modal
const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await onLogin(email, password);
      onClose();
      setEmail('');
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      data-testid="login-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"></div>
      <div 
        data-testid="login-modal"
        className="glass-card p-8 w-full max-w-md relative z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="login-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-cyan-500/20 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-cyan-400" />
          </div>
          <h2 
            className="text-2xl font-semibold"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Zona Clientes
          </h2>
          <p className="text-slate-400 mt-2">Ingresa a tu cuenta</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm text-slate-400 mb-2">
                Email
              </label>
              <input
                type="email"
                id="login-email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="morphyng-input"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm text-slate-400 mb-2">
                Contrasena
              </label>
              <input
                type="password"
                id="login-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="morphyng-input"
                placeholder="Tu contrasena"
              />
            </div>

            {error && (
              <div 
                data-testid="login-error-message"
                className="p-3 rounded-lg bg-red-500/10 text-red-400 text-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Ingresando...
                </>
              ) : (
                <>
                  Ingresar
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Client Dashboard
const ClientDashboard = ({ user, onBack, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [dailySales, setDailySales] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      const token = localStorage.getItem('morphyng_token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [summaryRes, transactionsRes, dailyRes, settlementsRes] = await Promise.all([
          axios.get(`${API}/transactions/summary`, { headers }),
          axios.get(`${API}/transactions?limit=100`, { headers }),
          axios.get(`${API}/transactions/daily?days=14`, { headers }),
          axios.get(`${API}/settlements`, { headers })
        ]);

        setSummary(summaryRes.data);
        setTransactions(transactionsRes.data);
        setDailySales(dailyRes.data);
        setSettlements(settlementsRes.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const filteredTransactions = statusFilter === 'all' 
    ? transactions 
    : transactions.filter(t => t.status === statusFilter);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-emerald-500/20 text-emerald-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      approved: 'Aprobada',
      pending: 'Pendiente',
      rejected: 'Rechazada'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const maxSale = Math.max(...dailySales.map(d => d.amount), 1);

  return (
    <div 
      data-testid="client-dashboard"
      className="min-h-screen bg-slate-950 pt-24 pb-12"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <button
              data-testid="dashboard-back-btn"
              onClick={onBack}
              className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 mb-4"
            >
              <ChevronRight className="rotate-180" size={18} />
              Volver al inicio
            </button>
            <h1 
              className="text-3xl md:text-4xl font-semibold"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Hola, <span className="text-cyan-400">{user?.contact_name}</span>
            </h1>
            <p className="text-slate-400 mt-1">{user?.company_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-colors"
              title="Actualizar datos"
            >
              <RefreshCw size={20} />
            </button>
            <button
              data-testid="dashboard-logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              <LogOut size={18} />
              Salir
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Ventas del Mes</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {formatCurrency(summary?.total_sales || 0)}
                </div>
                <div className="flex items-center gap-1 mt-2 text-emerald-400 text-sm">
                  <ArrowUpRight size={16} />
                  <span>{summary?.approved_count || 0} aprobadas</span>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Transacciones</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {summary?.total_transactions || 0}
                </div>
                <div className="flex items-center gap-1 mt-2 text-slate-400 text-sm">
                  <span>Ultimos 30 dias</span>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Pendientes</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {formatCurrency(summary?.pending_amount || 0)}
                </div>
                <div className="flex items-center gap-1 mt-2 text-yellow-400 text-sm">
                  <span>{summary?.pending_count || 0} en proceso</span>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-indigo-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Comisiones</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {formatCurrency(summary?.total_fees || 0)}
                </div>
                <div className="flex items-center gap-1 mt-2 text-slate-400 text-sm">
                  <span>2.9% por transaccion</span>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="glass-card p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Ventas - Ultimos 14 dias
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Calendar size={16} />
                  <span>{formatDate(dailySales[0]?.date)} - {formatDate(dailySales[dailySales.length - 1]?.date)}</span>
                </div>
              </div>
              
              <div className="flex items-end gap-2 h-48">
                {dailySales.map((day, index) => (
                  <div 
                    key={day.date} 
                    className="flex-1 flex flex-col items-center gap-2"
                  >
                    <div className="w-full flex flex-col items-center">
                      <span className="text-xs text-slate-500 mb-1 hidden md:block">
                        {day.amount > 0 ? formatCurrency(day.amount) : ''}
                      </span>
                      <div 
                        className="w-full bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t-sm transition-all duration-300 hover:from-cyan-400 hover:to-cyan-300 cursor-pointer min-h-[4px]"
                        style={{ 
                          height: `${Math.max((day.amount / maxSale) * 150, 4)}px`
                        }}
                        title={`${formatDate(day.date)}: ${formatCurrency(day.amount)} (${day.transactions} transacciones)`}
                      />
                    </div>
                    <span className="text-xs text-slate-500 transform -rotate-45 origin-center">
                      {new Date(day.date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-slate-800">
              <button
                onClick={() => setActiveTab('overview')}
                className={`pb-4 px-2 font-medium transition-colors ${
                  activeTab === 'overview' 
                    ? 'text-cyan-400 border-b-2 border-cyan-400' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Transacciones
              </button>
              <button
                onClick={() => setActiveTab('settlements')}
                className={`pb-4 px-2 font-medium transition-colors ${
                  activeTab === 'settlements' 
                    ? 'text-cyan-400 border-b-2 border-cyan-400' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Liquidaciones
              </button>
            </div>

            {activeTab === 'overview' && (
              <div className="glass-card overflow-hidden">
                {/* Filters */}
                <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Filter size={18} className="text-slate-400" />
                    <span className="text-slate-400 text-sm">Filtrar por estado:</span>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500"
                    >
                      <option value="all">Todas</option>
                      <option value="approved">Aprobadas</option>
                      <option value="pending">Pendientes</option>
                      <option value="rejected">Rechazadas</option>
                    </select>
                  </div>
                  <div className="text-slate-400 text-sm">
                    Mostrando {filteredTransactions.length} transacciones
                  </div>
                </div>

                {/* Transactions Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left p-4 text-slate-400 font-medium text-sm">Fecha</th>
                        <th className="text-left p-4 text-slate-400 font-medium text-sm">Descripcion</th>
                        <th className="text-left p-4 text-slate-400 font-medium text-sm">Medio de Pago</th>
                        <th className="text-right p-4 text-slate-400 font-medium text-sm">Monto</th>
                        <th className="text-right p-4 text-slate-400 font-medium text-sm">Comision</th>
                        <th className="text-center p-4 text-slate-400 font-medium text-sm">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.slice(0, 20).map((tx, index) => (
                        <tr 
                          key={tx.id} 
                          className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors"
                        >
                          <td className="p-4">
                            <div className="text-white text-sm">{formatDate(tx.date)}</div>
                            <div className="text-slate-500 text-xs">{formatTime(tx.date)}</div>
                          </td>
                          <td className="p-4 text-slate-300 text-sm">{tx.description}</td>
                          <td className="p-4">
                            <div className="text-white text-sm">{tx.payment_method}</div>
                            {tx.card_last_four && (
                              <div className="text-slate-500 text-xs">****{tx.card_last_four}</div>
                            )}
                          </td>
                          <td className="p-4 text-right text-white font-medium">
                            {formatCurrency(tx.amount)}
                          </td>
                          <td className="p-4 text-right text-slate-400 text-sm">
                            -{formatCurrency(tx.fee)}
                          </td>
                          <td className="p-4 text-center">
                            {getStatusBadge(tx.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {filteredTransactions.length > 20 && (
                  <div className="p-4 border-t border-slate-800 text-center">
                    <span className="text-slate-400 text-sm">
                      Mostrando 20 de {filteredTransactions.length} transacciones
                    </span>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settlements' && (
              <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left p-4 text-slate-400 font-medium text-sm">Fecha</th>
                        <th className="text-left p-4 text-slate-400 font-medium text-sm">Transacciones</th>
                        <th className="text-right p-4 text-slate-400 font-medium text-sm">Monto</th>
                        <th className="text-center p-4 text-slate-400 font-medium text-sm">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlements.map((settlement) => (
                        <tr 
                          key={settlement.id} 
                          className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors"
                        >
                          <td className="p-4">
                            <div className="text-white text-sm">{formatDate(settlement.date)}</div>
                          </td>
                          <td className="p-4 text-slate-300 text-sm">
                            {settlement.transactions_count} operaciones
                          </td>
                          <td className="p-4 text-right text-white font-medium text-lg">
                            {formatCurrency(settlement.amount)}
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              settlement.status === 'completed' 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {settlement.status === 'completed' ? 'Acreditada' : 'Pendiente'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Data Notice */}
            <div className="mt-6 p-4 rounded-lg bg-slate-800/30 border border-slate-700">
              <p className="text-slate-400 text-sm text-center">
                Los datos mostrados son de ejemplo para demostrar las funcionalidades del dashboard.
                En produccion se conectara con las transacciones reales de tu comercio.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const { user, login, logout, isAuthenticated, loading } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleLogout = () => {
    logout();
    setShowDashboard(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (showDashboard && isAuthenticated) {
    return (
      <>
        <Navigation 
          onLoginClick={() => setShowLoginModal(true)}
          isAuthenticated={isAuthenticated}
          onDashboardClick={() => setShowDashboard(true)}
          onLogout={handleLogout}
          user={user}
        />
        <ClientDashboard 
          user={user}
          onBack={() => setShowDashboard(false)}
          onLogout={handleLogout}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation 
        onLoginClick={() => setShowLoginModal(true)}
        isAuthenticated={isAuthenticated}
        onDashboardClick={() => setShowDashboard(true)}
        onLogout={handleLogout}
        user={user}
      />
      <main>
        <HeroSection />
        <ServicesSection />
        <HowItWorksSection />
        <BenefitsSection />
        <ContactSection />
      </main>
      <Footer onOpenTerms={() => setShowTermsModal(true)} />

      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={login}
      />

      <TermsModal 
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
}

export default App;
